import React, { Component } from 'react';
import { scaleOrdinal, schemeCategory20 } from 'd3-scale';
import PropTypes from 'prop-types';
import { sankey, sankeyLinkHorizontal } from 'd3-sankey';
import { numberFormatTwoFractions } from '../../helper/dateFormat';
import { withStyles } from 'material-ui/styles';

const styles = theme => ({
  chart__sankey: {
    margin: '0 auto',
    display: 'block'
  },
  chart__headline: {
    fill: theme.palette.text.primary,
    fontWeight: 400
  },
  node__text: {
    fontSize: '0.75rem',
    fill: theme.palette.text.primary
  },
  node__rect: {
    cursor: 'pointer'
  },
  node__link: {
    strokeOpacity: theme.palette.type ? 0.3 : 0.75,
    transition: 'stroke-opacity 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
    '&:hover': {
      strokeOpacity: 0.55
    }
  },
  node__linkRect: {
    transition: 'fill-opacity 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
    fillOpacity: theme.palette.type ? 0.3 : 0.75,
    '&:hover': {
      fillOpacity: 0.55
    }
  },
  link__rect: {
    fillOpacity: 0.6,
    transition: 'fill-opacity 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
    '&hover': {
      fillOpacity: 0.55
    }
  }
});

const color = scaleOrdinal(schemeCategory20);
const getColor = d => (d.color = color(d.name.replace(/ .*/, '')));

const LinearGradients = ({ links }) => {
  return (
    <defs>
      {links.map((item, i) => {
        const gradientId = `gradient${i}`;
        return (
          <linearGradient key={`linear${gradientId}`} id={gradientId}>
            <stop offset={0.1} stopColor={item.source.color} />
            <stop offset={0.9} stopColor={item.target.color} />
          </linearGradient>
        );
      })}
    </defs>
  );
};

const linkToChart = ({ history, match, group, name }) =>
  history.push(
    `${match.url}/chart/group/${group}/filter/${encodeURIComponent(name)}`
  );

const ChartHeaders = ({ width, headers, classes }) => {
  const positionX = Math.round(width / (headers.length - 1));
  const lastEntry = headers.length - 1;

  return (
    <g transform="translate(0, 20)" width={width}>
      {headers.map((entry, i) => {
        if (i === 0) {
          return (
            <text className={classes.chart__headline} key={i} x={0}>
              {entry}
            </text>
          );
        }

        if (i === lastEntry) {
          return (
            <text
              className={classes.chart__headline}
              key={i}
              x={width}
              textAnchor="end"
            >
              {entry}
            </text>
          );
        }

        return (
          <text
            className={classes.chart__headline}
            key={i}
            x={`${positionX * i}`}
            textAnchor="middle"
          >
            {entry}
          </text>
        );
      })}
    </g>
  );
};

const getNodeText = ({
  node,
  sankeyOptions: { showValues, showCosts, showNames },
  unit,
  cost,
  currency
}) => {
  let label = [];

  if (showNames) {
    const name = node.name;
    label.push(name);
  }

  if (showValues) {
    const formattedValues = `${numberFormatTwoFractions.format(
      node.value
    )} ${unit}`;
    label.push(formattedValues);
  }

  if (showCosts) {
    const formattedCosts = `${numberFormatTwoFractions.format(
      node.value * cost / 100
    )} ${currency}`;
    label.push(formattedCosts);
  }

  return label.join(' / ');
};

const ChartNodes = ({
  nodes,
  width,
  unit,
  match,
  history,
  sankeyOptions,
  cost,
  currency,
  classes
}) => {
  if (nodes.length === 0) {
    return <g className="nodes" />;
  }

  return (
    <g className="nodes">
      {nodes.map((node, i) => {
        const { value, name, group, x0, y0, x1, y1 } = node;

        return (
          <g className="node" key={`sankeyNode${i}`}>
            <rect
              onClick={() => linkToChart({ history, match, group, name })}
              className={classes.node__rect}
              x={x0}
              y={y0}
              height={y1 - y0}
              width={x1 - x0}
              style={{ fill: getColor(node) }}
            >
              <title>{`${name}\n${numberFormatTwoFractions.format(
                value
              )} ${unit}`}</title>
            </rect>
            {node.x0 < width / 2 ? (
              <text
                className={classes.node__text}
                x={node.x0 + 16}
                y={(y1 + y0) / 2}
                dy={'.35rem'}
                textAnchor="start"
              >
                {getNodeText({
                  sankeyOptions,
                  node,
                  unit,
                  cost,
                  currency
                })}
              </text>
            ) : (
              <text
                className={classes.node__text}
                x={node.x1 - 16}
                y={(y1 + y0) / 2}
                dy={'.35rem'}
                textAnchor="end"
              >
                {getNodeText({
                  sankeyOptions,
                  node,
                  unit,
                  cost,
                  currency
                })}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
};

const ChartLinks = ({ links, unit, classes }) => {
  if (links.length === 0) {
    return <g className="links" />;
  }

  return (
    <g className="links">
      {links.map((link, i) => {
        if (link.y1.toFixed(3) - link.y0.toFixed(3) !== 0) {
          return (
            <path
              key={i}
              className={classes.node__link}
              d={sankeyLinkHorizontal()(link)}
              style={{
                fill: 'none',
                strokeWidth: Math.max(1, link.width),
                stroke: (i => `url(#gradient${i})`)(i)
              }}
            >
              <title>{`${link.source.name} → ${link.target
                .name}\n${numberFormatTwoFractions.format(
                link.value
              )} ${unit}`}</title>
            </path>
          );
        }

        return (
          <rect
            key={i}
            className={classes.node__linkRect}
            x={link.source.x1}
            y={link.y0 - Math.max(1, link.width) / 2}
            width={link.target.x0 - link.source.x1}
            height={Math.max(1, link.width)}
            style={{
              fill: (i => `url(#gradient${i})`)(i)
            }}
          >
            <title>{`${link.source.name} → ${link.target
              .name}\n${numberFormatTwoFractions.format(
              link.value
            )} ${unit}`}</title>
          </rect>
        );
      })}
    </g>
  );
};

class SankeyChart extends Component {
  constructor(props) {
    super(props);

    this.state = {
      nodes: [],
      links: [],
      headers: [],
      loading: true,
      width: 560,
      height: 280,
      unit: 'kWh'
    };

    this.updateDimensions = this.updateDimensions.bind(this);
  }

  updateDimensions() {
    const parentWidth = this.refs.chartContainer.offsetWidth;

    if (this.state.width === parentWidth) {
      return;
    }

    if (parentWidth <= 560) {
      return;
    }

    const width = parentWidth - 32;
    this.setState({ width });
  }

  componentDidMount() {
    this.updateDimensions();
    window.addEventListener('resize', this.updateDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  componentWillReceiveProps(nextProps) {
    const { nodes, links, headers } = nextProps.data;
    if (nodes.length && links.length) {
      // clone data to avoid mutation of redux data
      const clonedNodes = JSON.parse(JSON.stringify(nodes));
      const clonedLinks = JSON.parse(JSON.stringify(links));

      this.setState({
        loading: false,
        nodes: clonedNodes,
        links: clonedLinks,
        headers
      });
    } else {
      this.setState({ loading: true });
    }
  }

  nodesWithElements = nodes =>
    nodes.reduce((m, element) => {
      const depth = element.depth;

      if (m.get(depth) === undefined) {
        m.set(depth, 0);
      }

      const prev = m.get(depth);
      m.set(depth, prev + 1);

      return m;
    }, new Map());

  findMaxNodeCount = nodesCount => Math.max(...nodesCount.values());
  calculateSankeyHeight = ({ nodes, width }) => {
    const maxElements = this.findMaxNodeCount(this.nodesWithElements(nodes));
    return width / 3 + maxElements * 11.5;
  };

  render() {
    const {
      sankeyOptions,
      cost,
      currency,
      match,
      history,
      classes
    } = this.props;
    const { width, unit, nodes, links } = this.state;

    if (this.props.aggregationLevel === 'none') {
      return (
        <div>
          Please choose another time frame with a lower aggregation level.
        </div>
      );
    }

    const height =
      this.state.nodes.length > 0
        ? this.calculateSankeyHeight({ nodes, width })
        : 560;

    this.sankey = sankey()
      .nodeWidth(15)
      .nodePadding(10)
      .extent([[1, 30], [this.state.width - 2, height - 10]]);

    const graph = this.sankey(Object.assign({}, { nodes, links }));

    return (
      <div className="align align--center" ref="chartContainer">
        <svg className="chart__sankey" width={this.state.width} height={height}>
          <ChartHeaders
            width={this.state.width}
            headers={this.state.headers}
            classes={classes}
          />

          <ChartLinks
            links={graph.links}
            unit={this.state.unit}
            classes={classes}
          />

          <ChartNodes
            nodes={graph.nodes}
            width={width}
            unit={unit}
            sankeyOptions={sankeyOptions}
            cost={cost}
            currency={currency}
            match={match}
            history={history}
            classes={classes}
          />
          <LinearGradients links={graph.links} />
        </svg>
      </div>
    );
  }
}

SankeyChart.propTypes = {
  data: PropTypes.shape({
    links: PropTypes.array.isRequired,
    nodes: PropTypes.array.isRequired
  }),
  aggregationLevel: PropTypes.string
};

export default withStyles(styles, { name: 'SankeyChart' })(SankeyChart);



// WEBPACK FOOTER //
// ./src/components/charts/SankeyChart.js